import { Button, FormGroup, InputGroup, Intent } from '@blueprintjs/core';
import { SettingsPanel } from '@/components/Settings/SettingsPanel';
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganisationContext, FederationContext, AnalyticsContext } from '../../../context';
import { useMutation, useQuery } from '../../../hooks';
import {
  UPDATE_ORGANISATION_REPORT_COLORS,
  UPDATE_FEDERATION_REPORT_COLORS,
} from '../../../mutations';
import { GET_ORGANISATIONS, GET_FEDERATIONS } from '../../../queries';
import { AppToaster } from '../../../utils/toaster';

interface ReportColorsEditorProps {
  organisation?: any;
  federation?: any;
}

const ReportColorsEditor: React.FC<ReportColorsEditorProps> = ({
  organisation: orgProp,
  federation: fedProp,
}) => {
  const { t } = useTranslation();
  const organisation = useContext(OrganisationContext) || orgProp;
  const federation = useContext(FederationContext) || fedProp;
  const analytics = useContext(AnalyticsContext);

  // Determine if editing federation or organisation
  // If both exist, prefer organisation (federation colors can be set separately)
  const isFederation = !!federation && (!organisation || fedProp);
  const entity = isFederation ? federation : organisation;
  const entityKey = isFederation ? federation?.key : organisation?.key;
  const entityId = isFederation ? federation?.id : organisation?.id;

  const query = isFederation ? GET_FEDERATIONS : GET_ORGANISATIONS;
  const querySelector = isFederation
    ? 'federations[0]'
    : 'organisations[0]';

  const { data: entityData, loading, error } = useQuery(query, {
    selector: querySelector,
    variables: {
      [isFederation ? 'federationKey' : 'organisationKey']: entityKey,
    },
  });

  // Format colors for display (add # if missing)
  const formatColorForDisplay = (color?: string) => {
    if (!color) return '';
    return color.startsWith('#') ? color : `#${color}`;
  };

  const reportColors = entityData?.reportColors || {};

  const [colors, setColors] = useState({
    red: formatColorForDisplay(reportColors?.red) || '#FF0000',
    amber: formatColorForDisplay(reportColors?.amber) || '#FFA500',
    green: formatColorForDisplay(reportColors?.green) || '#00FF00',
  });

  // Update colors when data loads
  useEffect(() => {
    if (entityData?.reportColors) {
      setColors({
        red: formatColorForDisplay(entityData.reportColors.red) || '#FF0000',
        amber: formatColorForDisplay(entityData.reportColors.amber) || '#FFA500',
        green: formatColorForDisplay(entityData.reportColors.green) || '#00FF00',
      });
    }
  }, [entityData]);

  const [updateOrganisationColors] = useMutation(
    UPDATE_ORGANISATION_REPORT_COLORS,
    {
      selector: 'updateOrganisationReportColors',
      refetchQueries: [
        {
          query: GET_ORGANISATIONS,
          variables: { organisationKey: organisation?.key },
        },
      ],
    }
  );

  const [updateFederationColors] = useMutation(
    UPDATE_FEDERATION_REPORT_COLORS,
    {
      selector: 'updateFederationReportColors',
      refetchQueries: [
        {
          query: GET_FEDERATIONS,
          variables: { federationKey: federation?.key },
        },
      ],
    }
  );

  const handleColorChange = (field: 'red' | 'amber' | 'green', value: string) => {
    setColors((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const reportColorsInput = {
        red: colors.red?.replace('#', '') || undefined,
        amber: colors.amber?.replace('#', '') || undefined,
        green: colors.green?.replace('#', '') || undefined,
      };

      if (isFederation) {
        await updateFederationColors({
          variables: {
            federationId: entityId,
            reportColors: reportColorsInput,
          },
        });
        (analytics as any)?.events?.reporting?.updateFederationReportColors?.();
      } else {
        await updateOrganisationColors({
          variables: {
            organisationId: entityId,
            reportColors: reportColorsInput,
          },
        });
        (analytics as any)?.events?.reporting?.updateOrganisationReportColors?.();
      }

      AppToaster.show({
        message: t('Report colors saved successfully'),
        intent: Intent.SUCCESS,
        icon: 'tick',
      });
    } catch (error) {
      AppToaster.show({
        message: t('Error saving report colors'),
        intent: Intent.DANGER,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    setColors({
      red: '#FF0000',
      amber: '#FFA500',
      green: '#00FF00',
    });
  };

  if (loading) {
    return <SettingsPanel title={t('Report Colors')}>Loading...</SettingsPanel>;
  }

  if (error) {
    return (
      <SettingsPanel title={t('Report Colors')}>
        Error loading report colors
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel title={t('Report Colors')}>
      <div style={{ maxWidth: '600px' }}>
        <p style={{ marginBottom: '20px' }}>
          {t(
            'Configure the colors used in exported reports for Red (error/not complete), Amber (warning/required), and Green (complete/satisfactory) status indicators.'
          )}
        </p>

        <FormGroup label={t('Red Color')} labelFor="red-color">
          <InputGroup
            id="red-color"
            type="color"
            value={colors.red}
            onChange={(e) => handleColorChange('red', e.target.value)}
            style={{ width: '100px', height: '40px' }}
          />
          <InputGroup
            value={colors.red}
            onChange={(e) => handleColorChange('red', e.target.value)}
            placeholder="#FF0000"
            style={{ marginTop: '10px', width: '200px' }}
          />
          <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            {t('Used for invalid/not complete/expired status')}
          </p>
        </FormGroup>

        <FormGroup label={t('Amber Color')} labelFor="amber-color">
          <InputGroup
            id="amber-color"
            type="color"
            value={colors.amber}
            onChange={(e) => handleColorChange('amber', e.target.value)}
            style={{ width: '100px', height: '40px' }}
          />
          <InputGroup
            value={colors.amber}
            onChange={(e) => handleColorChange('amber', e.target.value)}
            placeholder="#FFA500"
            style={{ marginTop: '10px', width: '200px' }}
          />
          <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            {t('Used for warning/required/in progress status')}
          </p>
        </FormGroup>

        <FormGroup label={t('Green Color')} labelFor="green-color">
          <InputGroup
            id="green-color"
            type="color"
            value={colors.green}
            onChange={(e) => handleColorChange('green', e.target.value)}
            style={{ width: '100px', height: '40px' }}
          />
          <InputGroup
            value={colors.green}
            onChange={(e) => handleColorChange('green', e.target.value)}
            placeholder="#00FF00"
            style={{ marginTop: '10px', width: '200px' }}
          />
          <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            {t('Used for complete/satisfactory/valid status')}
          </p>
        </FormGroup>

        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <Button
            intent={Intent.PRIMARY}
            onClick={handleSave}
            icon="floppy-disk"
          >
            {t('Save')}
          </Button>
          <Button onClick={handleReset} icon="reset">
            {t('Reset to Defaults')}
          </Button>
        </div>
      </div>
    </SettingsPanel>
  );
};

export default ReportColorsEditor;
